import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { TID, LOGO_URL } from "@/lib/testIds";

const LINKS = [
  { href: "/#services", label: "Services" },
  { href: "/#case-studies", label: "Case Studies" },
  { href: "/#why", label: "Why Us" },
  { href: "/#about", label: "About" },
  { href: "/changelog", label: "Changelog" },
  { href: "/#contact", label: "Contact" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid={TID.nav.root}
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-navy/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid={TID.nav.logo} className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="MSMEmoney" className="h-6 md:h-8 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={TID.nav.link(l.label.toLowerCase().replace(/\s+/g, "-"))}
              className="font-mono text-[11px] uppercase tracking-[0.24em] text-navy/75 hover:text-[color:var(--violet)] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid={TID.nav.cta}
            className="hidden sm:inline-flex items-center gap-2 bg-[color:var(--gold)] px-4 py-2.5 text-navy hover:bg-navy hover:text-white transition-colors"
          >
            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.24em]">
              Book consultation
            </span>
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <button
            aria-label="Menu"
            data-testid={TID.nav.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid h-10 w-10 place-items-center border border-navy/20 text-navy"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={TID.nav.mobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-navy/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl text-navy hover:text-[color:var(--violet)]"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
